import {ProofType} from '../../constants/enumerations';
import {
  IBalanceDetail,
  IBalanceSheetItem,
  ICandidateOrderItem,
  IClient,
  IDebtorItem,
  IHistoryDetail,
  INewClientRequest,
  IOrderDetail,
  IOrderListItem,
  IReceivable,
  ISalesmanClient,
  IScheduleItem,
  ISemaphoreValue,
} from '../../interfaces/dtos';
import IInvoiceCandidate from '../../interfaces/dtos/invoice-candidate-list.interface';
import {IInvoiceItem} from '../../interfaces/dtos/invoice-item.interface';
import {IInvoiceRequest} from '../../interfaces/dtos/invoice-request.interface';
import {IProofPaymentRequest} from '../../interfaces/dtos/proof-payment-request.interface';
import {
  IRequestsToSync,
  IRequestsToSyncItem,
} from '../../interfaces/dtos/requests-to-sync.interface';
import {IDocumentClient} from '../../interfaces/dtos/salesman-client.interface';
import {IPaymentForm, ITaxForm} from '../../interfaces/forms';
import {IOrderRequest} from '../../interfaces/services/order-request.interface';
import {ResultOfRequestCached} from '../../interfaces/types/result-of-request-cached';
import {WithoutConnectionWorkflowType} from '../../interfaces/types/without-connection-workflow.type';
import {IProfile} from '../user/Types';
import {
  IListOption,
  IOrder,
  IOrderItem,
  IProduct,
  ICategory,
  IPortfolioFilter,
  IStatusFilter,
} from '../commons/interfaces';

/**
 *
 */
export interface IState {
  errors: any[];
  receivables: IReceivable[] | null;
  semaphoreItems: ISemaphoreValue[] | null;
  balances: IBalanceDetail[] | null;
  clients: IClient[] | null;
  salesTerritories: IListOption[];
  customerTypes: IListOption[];
  salesCities: IListOption[];
  salesCategories: ICategory[];
  salesmanNewClientRequest: ISalesmanClient | null;
  customerRequiredDocumentList: IListOption[];
  customerRequiredDocumentsHelp: string | null;
  customerPaymentOptions: IListOption[];
  salesmanProductList: IProduct[] | null;
  salesmanProductToAdd: IProduct | null;
  customerOrder: IOrder | null;
  salesmanOrderItemCandidate: IOrderItem | null;
  balanceCustomer: IBalanceSheetItem | null;
  client: IClient | undefined;
  balanceSheet: IBalanceSheetItem | null;
  history: IHistoryDetail[] | null;
  debtors: IDebtorItem[] | null;
  salesmanPortfolioFilter: IPortfolioFilter | null;
  orderList: IOrderListItem[] | null;
  orderDetail: IOrderDetail | null;
  salesmanOrderFilter: IStatusFilter | null;
  schedule: IScheduleItem[] | null;
  appointment: IScheduleItem | null;
  invoiceCandidates: IInvoiceCandidate[] | null;
  invoiceCandidate: IInvoiceCandidate | null;
  invoiceRequest: IInvoiceRequest | null;
  invoiceItemEditing: IInvoiceItem | null;
  requestsToSync: IRequestsToSync | null;
  orderCandidates: ICandidateOrderItem[] | null;
}

/**
 *
 */
export interface IContext extends IState {
  setSalesmanOrderFilter: (status: IStatusFilter | null) => void;
  setSalesmanOrderDetail: (detail: IOrderDetail | null) => void;
  fetchSalesmanOrderDetail: (id: string) => Promise<boolean>;
  fetchCandidateOrderDetail: (id: string) => Promise<boolean>;
  fetchSalesmanOrderList: () => Promise<boolean>;
  fetchSalesmanProducts: (
    priceListId?: string,
    paymentOptionId?: string,
  ) => Promise<boolean>;
  clearSalesmanProducts: () => void;
  fetchClients: (read?: boolean) => Promise<boolean>;
  fetchBalanceList: () => Promise<boolean>;
  setSalesmanNewClientRequest: (request: ISalesmanClient) => void;
  sendSalesmanNewClientRequest: (
    documents: IDocumentClient[],
    onWithoutConnection?: (request: any) => void,
  ) => Promise<ResultOfRequestCached>;
  setSalesmanProductToAdd: (product: IProduct | null) => void;
  createSalesmanNewOrder: (client: IClient) => void;
  updateSalesmanNewOrder: (order: IOrder | null) => void;
  updateCandidateToShoppingCart: (item: IOrderItem | null) => void;
  sendSalesmanNewOrder: (order: IOrder) => Promise<ResultOfRequestCached>;
  resetNewOrder: () => void;
  setSalesmanClient: (client: IClient | null) => void;
  fetchBalanceCustomer: (client: IClient) => Promise<boolean>;
  getProfile: () => IProfile | null;
  fetchBalanceSheet: (filter?: IPortfolioFilter | null) => Promise<boolean>;
  fetchResources: () => Promise<boolean>;
  fetchHistoryList: () => Promise<boolean>;
  fetchSemaphoreItems: (filter?: IPortfolioFilter | null) => Promise<boolean>;
  fetchDebtorList: (
    key: string,
    filter?: IPortfolioFilter | null,
  ) => Promise<boolean>;
  fetchReceivableListByDebtor: (key: string, rif: string) => Promise<boolean>;
  setPortfolioFilter: (filter: IPortfolioFilter | null) => void;
  fecthSchedule: () => Promise<boolean>;
  setAppointment: (status: IScheduleItem | null) => void;
  sendAppointConfirmation: (code: string | null) => Promise<boolean>;
  sendProof: (
    form: IPaymentForm | ITaxForm,
    type: ProofType,
  ) => Promise<ResultOfRequestCached>;
  actions?: typeof ACTION_VERBS;
  fetchInvoiceCandidates: () => Promise<boolean>;
  setInvoiceCandidate: (candidate: IInvoiceCandidate | null) => void;
  setInvoiceRequest: (request: IInvoiceRequest | null) => void;
  setInvoiceItemEditing: (items: IInvoiceItem | null) => void;
  sendInvoiceRequest: () => Promise<boolean>;
  saveRequestToServer: (
    workflow: WithoutConnectionWorkflowType,
    value: INewClientRequest | IProofPaymentRequest | IOrderRequest,
  ) => Promise<boolean>;
  retrieveRequestToServer: () => Promise<boolean>;
  clearRequestToServer: () => Promise<boolean>;
  hasRequestsToSinchronize: () => boolean;
  syncNewClientRequest: (toSync: IRequestsToSyncItem) => Promise<boolean>;
  syncPaymentProofRequest: (toSync: IRequestsToSyncItem) => Promise<boolean>;
  syncSalesOrderRequest: (toSync: IRequestsToSyncItem) => Promise<boolean>;
  updateAllRequestToStorage: () => Promise<boolean>;
  fetchOrderCandidates: () => Promise<boolean>;
}

/**
 *
 */
export const ACTION_VERBS = {
  BALANCE_SHEET_SALESMAN: 'BALANCE_SHEET_SALESMAN',
  CLEAR_ERRORS: 'CLEAR_ERRORS',
  STATE_PROCESS_ERROR: 'STATE_PROCESS_ERROR',
  GET_SALESMAN_CLIENTS: 'GET_SALESMAN_CLIENTS',
  SALESMAN_NEW_CLIENT_REQUEST: 'SALESMAN_NEW_CLIENT_REQUEST',
  SEND_SALESMAN_NEW_CLIENT_REQUEST: 'SEND_SALESMAN_NEW_CLIENT_REQUEST',
  SET_SALESMAN_PRODUCTS: 'SET_SALESMAN_PRODUCTS',
  SET_SALESMAN_PRODUCT_TO_ADD: 'SET_SALESMAN_PRODUCT_TO_ADD',
  UPDATE_SALESMAN_NEW_ORDER: 'UPDATE_SALESMAN_NEW_ORDER',
  UPDATE_ORDER_ITEM_CANDIDATE_SHOPPING_CART:
    'UPDATE_ORDER_ITEM_CANDIDATE_SHOPPING_CART',
  SEND_SALESMAN_NEW_ORDER: 'SEND_SALESMAN_NEW_ORDER',
  SET_SALESMAN_CLIENT: 'SET_SALESMAN_CLIENT',
  FETCH_BALANCE_CLIENT: 'FETCH_BALANCE_CLIENT',
  BALANCE_SHEET: 'BALANCE_SHEET',
  SALESMAN_RESOURCES: 'SALESMAN_RESOURCES',
  CLIENT_HISTORY_LIST: 'CLIENT_HISTORY_LIST',
  CUSTOMER_BALANCE_LIST: 'CUSTOMER_BALANCE_LIST',
  SEMAPHORE_ITEMS: 'SEMAPHORE_ITEMS',
  DEBTOR_LIST: 'DEBTOR_LIST',
  RECEIVABLE_LIST: 'RECEIVABLE_LIST',
  SET_PORTFOLIO_FILTER: 'SET_PORTFOLIO_FILTER',
  GET_SALESMAN_ORDER_LIST: 'GET_SALESMAN_ORDER_LIST',
  GET_SALESMAN_ORDER_DETAIL: 'GET_SALESMAN_ORDER_DETAIL',
  SET_ORDER_FILTER: 'SET_ORDER_FILTER',
  SET_SCHEDULE: 'SET_SCHEDULE',
  FETCH_SCHEDULE: 'FETCH_SCHEDULE',
  SET_APPOINTMENT: 'SET_APPOINTMENT',
  FETCH_INVOICE_CANDIDATES: 'FETCH_INVOICE_CANDIDATES',
  SET_INVOICE_CANDIDATE: 'SET_INVOICE_CANDIDATE',
  SET_INVOICE_REQUEST: 'SET_INVOICE_REQUEST',
  SET_INVOICE_ITEMS_REMOVED: 'SET_INVOICE_ITEMS_REMOVED',
  SET_INVOICE_ITEMS_EDITING: 'SET_INVOICE_ITEMS_EDITING',
  SEND_SALESMAN_INVOICE_REQUEST: 'SEND_SALESMAN_INVOICE_REQUEST',
  RETRIEVE_REQUESTS_TO_SERVER: 'RETRIEVE_REQUESTS_TO_SERVER',
  CLEAR_REQUESTS_TO_SERVER: 'CLEAR_REQUESTS_TO_SERVER',
  UPDATED_REQUESTS_SAVED_TO_STORAGE: 'UPDATED_REQUESTS_SAVED_TO_STORAGE',
  SYNC_NEW_CLIENT_REQUEST: 'SYNC_NEW_CLIENT_REQUEST',
  UPDATED_ALL_REQUESTS_SAVED_TO_STORAGE:
    'UPDATED_ALL_REQUESTS_SAVED_TO_STORAGE',
  SEND_SALESMAN_NEW_PROOF_OF_PAYMENT: 'SEND_SALESMAN_NEW_PROOF_OF_PAYMENT',
  SYNC_PROOF_OF_PAYMENT_REPORT_REQUEST: 'SYNC_PROOF_OF_PAYMENT_REPORT_REQUEST',
  SYNC_SALES_ORDER_REQUEST: 'SYNC_SALES_ORDER_REQUEST',
  FETCH_ORDER_CANDIDATES: 'FETCH_ORDER_CANDIDATES',
  FETCH_CANDIDATE_ORDER_DETAIL: 'FETCH_CANDIDATE_ORDER_DETAIL',
};
